import { MAX_HOME_WORK_DIST_KM, MIN_HOME_WORK_DIST_KM } from '@commutifi-fe/constants'
import { IntlShape } from 'locales/index'
import { AddressGeocodeValue, Location } from '@commutifi-fe/interfaces'
import { getDistanceFromLatLngInKm } from '@commutifi-fe/utils'
import { validateGoogleAddressComponents } from '@commutifi-fe/commutifi-specific/validators/address'
import {
  AutocompleteErrorCodes,
  AutocompleteErrorsEnum
} from '@commutifi-fe/commutifi-specific/Presentation/AddressAutocomplete'
import { BuildingProps } from '@commutifi/models/Buildings'
import { omit } from 'lodash'
import { formatAddress } from 'utils/formatters'

export const renderBuildingAddress = (addr: Partial<BuildingProps> | undefined) =>
  addr ? addr.name || formatAddress(omit(addr, 'postalCode')) : '—'

/**
 * Checks if the home address option is selected (required) and if it's a valid option
 *    that is precise enough (Not a city, has a street name at least)
 *
 * @param rule - antd for item rule
 * @param address - home string or object value
 */
export const validateAddressOption = (
  _: any,
  address: AddressGeocodeValue | AutocompleteErrorCodes | null,
  options: { strict?: boolean; intl: IntlShape }
) => {
  const { intl, strict } = options
  if (address === AutocompleteErrorsEnum.NoResultsError) {
    return Promise.reject(intl.formatMessage({ id: 'onboarding.addressAutocomplete.validate.noResults' }))
  }

  if (address === AutocompleteErrorsEnum.FetchingError) {
    return Promise.reject(intl.formatMessage({ id: 'onboarding.addressAutocomplete.validate.failedFetchingOptions' }))
  }

  if (address === AutocompleteErrorsEnum.GeocodeError) {
    return Promise.reject(intl.formatMessage({ id: 'onboarding.addressAutocomplete.validate.failedGeocode' }))
  }

  // We must have an address defined
  if (!address) {
    return Promise.reject(intl.formatMessage({ id: 'onboarding.addressAutocomplete.error.required' }))
  }

  if (typeof address === 'object') {
    if (!address.formattedAddress) {
      return Promise.reject(intl.formatMessage({ id: 'onboarding.addressAutocomplete.error.required' }))
    }

    // Verify the address is specific enough (has a street/route, intersection or zipcode)
    if (address.addressComponents && !validateGoogleAddressComponents(address.addressComponents, options)) {
      if (strict) {
        return Promise.reject(intl.formatMessage({ id: 'onboarding.addressAutocomplete.validate.notPrecise.strict' }))
      }
      return Promise.reject(intl.formatMessage({ id: 'onboarding.addressAutocomplete.validate.notPrecise' }))
    }
  }

  return Promise.resolve()
}

/**
 * Checks that the distance with office address is reasonable and makes sense
 *
 * @param rule - antd for item rule
 * @param address - home string or object value
 * @param officeLocation - Office location bias
 */
export const validateAddressDistance = async (
  rule: any,
  address: AddressGeocodeValue | AutocompleteErrorCodes,
  options: { officeLocation?: Location | null; intl: IntlShape }
) => {
  const { officeLocation, intl } = options
  if (typeof address === 'object') {
    // Verify if the office address is too near or too far from the commuter home address
    if (officeLocation) {
      const distance = getDistanceFromLatLngInKm(address.location, officeLocation)

      if (distance < MIN_HOME_WORK_DIST_KM) {
        return Promise.reject(intl.formatMessage({ id: 'onboarding.homeAddressAutocomplete.validate.sameAsOffice' }))
      }
      if (distance > MAX_HOME_WORK_DIST_KM) {
        return Promise.reject(intl.formatMessage({ id: 'onboarding.homeAddressAutocomplete.validate.nearOffice' }))
      }
    }
  }

  // Everything is OK
  return Promise.resolve()
}

/**
 * Validate 2 things
 * 1. if the home address option is selected (required) and if it's a valid option
 *    that is precise enough (Not a city, has a street name at least)
 * 2. The distance with office address is reasonable and makes sense
 *
 * @param rule - antd for item rule
 * @param address - home string or object value
 * @param officeLocation - Office location bias
 */
export const validateAddress = async (
  rule: any,
  address: AddressGeocodeValue | AutocompleteErrorCodes,
  options: { officeLocation?: Location; strict?: boolean; intl: IntlShape }
) => {
  const { strict, officeLocation, intl } = options
  // We call address normal validation and then call specific validation
  // of the home address in the callback.
  try {
    await validateAddressOption(rule, address, { strict, intl })
  } catch (error) {
    return Promise.reject(error)
  }

  try {
    await validateAddressDistance(rule, address, { officeLocation, intl })
  } catch (error) {
    return Promise.reject(error)
  }

  // Everything is OK
  return Promise.resolve()
}
